'use client';

import { useAuiState } from '@assistant-ui/react';
import { FileText } from 'lucide-react';
import type { FC } from 'react';
import { useMemo } from 'react';
import { parseS3DocMetaMarkers, type S3DocMeta } from './types';

const S3DocCard: FC<{ doc: S3DocMeta }> = ({ doc }) => (
	<div className="flex items-center gap-2 rounded-xl border bg-muted px-3 py-2">
		<FileText className="size-4 shrink-0 text-muted-foreground" />
		<span className="truncate text-sm text-foreground">{doc.name}</span>
		<span className="shrink-0 rounded bg-muted-foreground/10 px-1.5 py-0.5 text-xs text-muted-foreground">{doc.fileType}</span>
	</div>
);

/**
 * Renders document cards for S3 attachments on user messages.
 * Scans message text parts for [S3_DOC_META:...] markers and
 * renders a card for each. Works for both live and history-loaded messages.
 */
export const UserMessageS3Documents: FC = () => {
	// Extract a stable JSON string from state to avoid infinite re-renders.
	// useAuiState uses reference equality — returning a new array each call
	// would cause an infinite update loop.
	const docsJson = useAuiState((s) => {
		// @ts-ignore — ScopeRegistry augmentation not resolved with moduleResolution "node"
		const content = s.message?.content;
		if (!Array.isArray(content)) return '';

		const allDocs: S3DocMeta[] = [];
		for (const part of content) {
			if (part.type === 'text' && typeof part.text === 'string') {
				allDocs.push(...parseS3DocMetaMarkers(part.text));
			}
		}
		if (allDocs.length === 0) return '';
		return JSON.stringify(allDocs);
	});

	// Deduplicate by documentId
	const uniqueDocs = useMemo(() => {
		if (!docsJson) return [];
		const docs: S3DocMeta[] = JSON.parse(docsJson);
		const seen = new Set<number>();
		return docs.filter((d) => {
			if (seen.has(d.documentId)) return false;
			seen.add(d.documentId);
			return true;
		});
	}, [docsJson]);

	if (uniqueDocs.length === 0) return null;

	return (
		<div className="col-span-full col-start-1 row-start-1 flex w-full flex-row flex-wrap justify-end gap-2">
			{uniqueDocs.map((doc: S3DocMeta) => (
				<S3DocCard doc={doc} key={doc.documentId} />
			))}
		</div>
	);
};
